import { useNgSession } from "@sazka/ng/ng-session";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { biRealtimeHttpClient } from "./bi-realtime-http-client";
import { BiRealtimeBonusesAttributes, BiRealtimeBonusesResponse } from "./bi-realtime-http-models";

const { session } = useNgSession();

export interface PlayersBonusInfo {
    bonuses: number[];
    isRestricted: boolean;
}

export namespace BiRealtimeHttpMetods {
    const getPlayersBonusAttributes = async (): Promise<[]> => {
        if (!session.value?.PlayerID) throw new Error("Session PlayerID is missing");

        const response = await biRealtimeHttpClient.get<BiRealtimeBonusesResponse>(
            `dehBonusInfo/${session.value.PlayerID}`
        );

        return response.data?.Attributes ?? [];
    };

    const getPlayersBonusesFromAttributes = (attributes: []): number[] => {
        // check every attribute for property with array of bonuses and merge them into one array
        return attributes.flatMap((bonusAttribute) =>
            Object.keys(bonusAttribute)
                .filter(
                    (key) =>
                        [
                            BiRealtimeBonusesAttributes.ActiveBonuses.toString(),
                            BiRealtimeBonusesAttributes.AttachedBonuses.toString(),
                            BiRealtimeBonusesAttributes.CompletedBonuses.toString(),
                            BiRealtimeBonusesAttributes.PartiallyCompeletedBonuses.toString(),
                        ].indexOf(key) > -1
                )
                .map((key) => bonusAttribute[key] ?? [])
                .flat()
                .map((bonusId) => Number(bonusId))
        );
    };

    export const getPlayersBonuses = async (): Promise<number[]> => {
        const attributes = await getPlayersBonusAttributes();
        return getPlayersBonusesFromAttributes(attributes);
    };

    const isPlayerRestrictedOnBonuses = (attributes: BiRealtimeBonusesAttributes[]): boolean => {
        const bonusDeniedAttribute = attributes.find((a) => !!a[BiRealtimeBonusesAttributes.BonusDenied])?.[
            BiRealtimeBonusesAttributes.BonusDenied
        ];
        const isBonusDenied = bonusDeniedAttribute === "1";

        const panicButtonAttribute = attributes.find((a) => !!a[BiRealtimeBonusesAttributes.PanicButton])?.[
            BiRealtimeBonusesAttributes.PanicButton
        ];

        let isPannicButtonBlocked = false;
        if (panicButtonAttribute === "Permanent") {
            isPannicButtonBlocked = true;
        } else if (panicButtonAttribute) {
            const panicButtonDate = new Date(panicButtonAttribute);
            if (!isNaN(panicButtonDate.getTime())) {
                isPannicButtonBlocked = panicButtonDate > new Date();
            }
        }

        return isBonusDenied || isPannicButtonBlocked;
    };

    export const getPlayersBonusesInfo = async (): Promise<PlayersBonusInfo> => {
        const attributes = await getPlayersBonusAttributes();
        const info: PlayersBonusInfo = {
            bonuses: getPlayersBonusesFromAttributes(attributes),
            isRestricted: isPlayerRestrictedOnBonuses(attributes),
        };

        return info;
    };

    export const confirmPlayerBonus = async (bonusId: number) => {
        await httpClient.post(`/api/player/bonuses/${bonusId}/confirm`);
    };
}
